import { useEffect, useState } from "react";
import { Download, Loader2, Image as ImageIcon, Camera, Gift, Wand2 } from "lucide-react";
import { renderGiftPromo } from "@/lib/promoCards";
import { api, imgSrc } from "@/lib/api";
import { renderPhotoPromo } from "@/lib/photoPromoImage";

const PROMOS = [
  ["app", "App-Werbung", "Videos schauen, Punkte sammeln, Geschenke gewinnen"],
  ["spiele", "Neue Spiele", "Rubbellose, Duelle & Glücksrad"],
  ["gratis-los", "Gratis-Rubbellos", "Willkommensgeschenk für neue Mitglieder"],
];
const FORMATS = [["quadrat", "Quadrat (Feed)"], ["story", "Story (Hochformat)"]];

const DownloadBtn = ({ href, name, testId }) => (
  <a href={href} download={name} data-testid={testId} className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-full pink-gradient text-white text-xs font-bold active:scale-95 transition-transform">
    <Download className="w-3.5 h-3.5" /> Herunterladen
  </a>
);

const Tile = ({ src, label, children, story, tall }) => (
  <div className="rounded-2xl border border-pink-100 bg-rose-50/40 p-3 space-y-2 fade-up">
    <div className={`rounded-xl overflow-hidden bg-rose-100 flex items-center justify-center ${tall ? "aspect-[9/16]" : story ? "aspect-[2/3]" : "aspect-square"}`}>
      {src ? <img src={src} alt={label} data-testid="promo-preview" loading="lazy" className="w-full h-full object-cover" /> : <Loader2 className="w-6 h-6 animate-spin text-rose-400" />}
    </div>
    <p className="text-xs font-bold text-slate-600">{label}</p>
    {children}
  </div>
);

function PhotoPromo({ format }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    renderPhotoPromo(format === "story" ? "story" : "square").then((b) => { u = URL.createObjectURL(b); setUrl(u); });
    return () => u && URL.revokeObjectURL(u);
  }, [format]);
  const label = FORMATS.find((f) => f[0] === format)[1];
  return (
    <Tile src={url} label={label} tall={format === "story"}>
      {url && <DownloadBtn href={url} name={`itzcouple-foto-${format}.png`} testId={`promo-download-foto-${format}`} />}
    </Tile>
  );
}

function GiftPromo({ r }) {
  const [urls, setUrls] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => urls && Object.values(urls).forEach(URL.revokeObjectURL), [urls]);
  const make = async () => {
    setBusy(true);
    const reward = { ...r, image: imgSrc(r.image) };
    const [sq, st] = await Promise.all([renderGiftPromo({ reward, format: "square" }), renderGiftPromo({ reward, format: "story" })]);
    setUrls({ quadrat: URL.createObjectURL(sq), story: URL.createObjectURL(st) }); setBusy(false);
  };
  const slug = r.title.toLowerCase().replace(/[^a-z0-9äöüß]+/g, "-").slice(0, 30);
  return (
    <li className="rounded-2xl border border-pink-100 p-3 space-y-3" data-testid={`promo-gift-${r.id}`}>
      <div className="flex items-center gap-3">
        {r.image ? <img src={imgSrc(r.image)} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <span className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center"><Gift className="w-5 h-5 text-rose-500" /></span>}
        <div className="flex-1 min-w-0"><p className="font-bold text-sm truncate">{r.title}</p><p className="text-xs text-amber-600 font-bold">{r.cost} Punkte</p></div>
        {!urls && <button type="button" onClick={make} disabled={busy} data-testid={`promo-gift-make-${r.id}`} className="h-9 px-3 rounded-full pink-gradient text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Bilder erstellen</button>}
      </div>
      {urls && (
        <div className="grid grid-cols-2 gap-3 max-w-md">
          {FORMATS.map(([f, label]) => (
            <Tile key={f} src={urls[f]} label={label} tall={f === "story"}>
              <DownloadBtn href={urls[f]} name={`itzcouple-geschenk-${slug}-${f}.png`} testId={`promo-gift-download-${r.id}-${f}`} />
            </Tile>
          ))}
        </div>
      )}
    </li>
  );
}

function GiftPromos() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/public/rewards").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4" data-testid="promo-gifts">
      <div>
        <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><Gift className="w-5 h-5 text-rose-500" /> Werbebild pro Geschenk</h3>
        <p className="text-sm text-slate-500">Für jedes aktive Geschenk ein fertiges Bild mit Punktepreis – als Quadrat und Story.</p>
      </div>
      {list === null ? <Loader2 className="w-5 h-5 animate-spin text-rose-400" /> : list.length === 0 ? <p className="text-sm text-slate-400">Noch keine aktiven Geschenke.</p>
        : <ul className="space-y-3">{list.map((r) => <GiftPromo key={r.id} r={r} />)}</ul>}
    </div>
  );
}

export function PromoAdmin() {
  return (
    <div className="space-y-6" data-testid="admin-promo">
      <GiftPromos />
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4">
        <div>
          <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><Camera className="w-5 h-5 text-rose-500" /> Werbebild mit eurem Foto</h3>
          <p className="text-sm text-slate-500">Euer Familienfoto bleibt unverändert – das Bild wird direkt hier erstellt.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 max-w-md">{FORMATS.map(([f]) => <PhotoPromo key={f} format={f} />)}</div>
      </div>
      {PROMOS.map(([key, title, desc]) => (
        <div key={key} className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4" data-testid={`promo-${key}`}>
          <div>
            <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-rose-500" /> {title}</h3>
            <p className="text-sm text-slate-500">{desc}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {FORMATS.map(([f, label]) => (
              <Tile key={f} src={`/promo/${key}-${f}.jpg`} label={label} story={f === "story"}>
                <DownloadBtn href={`/promo/${key}-${f}.jpg`} name={`itzcouple-${key}-${f}.jpg`} testId={`promo-download-${key}-${f}`} />
              </Tile>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
